"use client";

import Cookies from "js-cookie";
import { captureException } from "@/lib/error-tracker";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3200/api";

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const TOKEN_EXPIRY_KEY = "token_expiry";

export const getAccessToken = (): string | undefined => {
  return Cookies.get(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = (): string | undefined => {
  return Cookies.get(REFRESH_TOKEN_KEY);
};

export const setTokens = (
  accessToken: string,
  refreshToken: string | null,
  expiresIn: number
) => {
  // Store access token (expires in 1 hour)
  Cookies.set(ACCESS_TOKEN_KEY, accessToken, {
    expires: expiresIn / 86400, // Convert seconds to days
    path: "/",
    secure: process.env.NODE_ENV === "production",
    sameSite: "strict",
  });

  // Store refresh token (expires in 6 months)
  if (refreshToken) {
    Cookies.set(REFRESH_TOKEN_KEY, refreshToken, {
      expires: 180, // 6 months in days
      path: "/",
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict",
    });
  }

  // Store token expiry time
  const expiryTime = Date.now() + expiresIn * 1000;
  Cookies.set(TOKEN_EXPIRY_KEY, expiryTime.toString(), {
    expires: expiresIn / 86400,
    path: "/",
  });
};

export const clearTokens = () => {
  Cookies.remove(ACCESS_TOKEN_KEY, { path: "/" });
  Cookies.remove(REFRESH_TOKEN_KEY, { path: "/" });
  Cookies.remove(TOKEN_EXPIRY_KEY, { path: "/" });
};

export const isTokenExpired = (): boolean => {
  const expiryTime = Cookies.get(TOKEN_EXPIRY_KEY);
  if (!expiryTime) return true;

  const expiry = parseInt(expiryTime, 10);
  // Consider token expired 5 minutes before actual expiry for safety
  return Date.now() >= expiry - 5 * 60 * 1000;
};

// Refresh token state to prevent multiple simultaneous refresh calls
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// Track last 401 error log to prevent spam
let last401ErrorTime = 0;
const ERROR_LOG_COOLDOWN_MS = 5000; // Only log 401 errors once every 5 seconds

/**
 * Refresh the access token using the refresh token
 * POST /api/oauth/refresh
 */
export const refreshAccessToken = async (
  username: string
): Promise<string> => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await fetch(`${API_URL}/oauth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Refresh token is expired or invalid - user needs to login again
          clearTokens();
          throw new Error("Refresh token expired");
        }
        throw new Error(`Token refresh failed: ${response.statusText}`);
      }

      const data = await response.json();

      // Update access token (refresh endpoint returns null for refresh_token)
      setTokens(data.access_token, null, data.expires_in);

      return data.access_token;
    } catch (error) {
      clearTokens();

      // Only log if enough time has passed since last 401 error
      const now = Date.now();
      if (now - last401ErrorTime > ERROR_LOG_COOLDOWN_MS) {
        last401ErrorTime = now;
        captureException(error, {
          extra: { context: "Token refresh failed" },
        });
      }

      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export interface HttpClientOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body?: any;
  username?: string; // Required for token refresh
}

/**
 * HTTP client with automatic token refresh
 */
export const httpClient = async (
  url: string,
  options: HttpClientOptions = {}
): Promise<Response> => {
  const { method = "GET", headers = {}, body, username } = options;

  // Get access token
  let accessToken = getAccessToken();

  // Check if token needs refresh
  if (accessToken && isTokenExpired() && username) {
    try {
      accessToken = await refreshAccessToken(username);
    } catch (error) {
      // Token refresh failed, let the request proceed and handle 401
      // Note: refreshAccessToken already handles error logging with cooldown
    }
  }

  // Make request with auth token
  const requestHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...headers,
  };

  if (accessToken) {
    requestHeaders["Authorization"] = `Bearer ${accessToken}`;
  }

  let response = await fetch(url, {
    method,
    headers: requestHeaders,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Handle 401 - Check /ping to differentiate auth vs authz
  if (response.status === 401 && username && accessToken) {
    try {
      // Step 1: Call /ping endpoint to check if it's an auth issue (token expired)
      // or an authz issue (user lacks permissions)
      const pingResponse = await fetch(`${API_URL}/oauth/ping`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (pingResponse.status === 401) {
        // Ping failed with 401 → Token is expired, try to refresh
        const newAccessToken = await refreshAccessToken(username);

        // Retry the original request with new token
        requestHeaders["Authorization"] = `Bearer ${newAccessToken}`;

        response = await fetch(url, {
          method,
          headers: requestHeaders,
          body: body ? JSON.stringify(body) : undefined,
        });

        // If still 401 after refresh, user has no access to this resource
        if (response.status === 401) {
          throw new Error("User has no access to requested resource - insufficient permissions");
        }
      } else {
        // Ping succeeded but original request was 401 → User lacks permissions
        throw new Error("User has no access to requested resource - insufficient permissions");
      }
    } catch (error) {
      // If error message is about permissions, don't clear tokens
      if (error instanceof Error && error.message.includes("no access")) {
        // Only log if enough time has passed since last 401 error
        const now = Date.now();
        if (now - last401ErrorTime > ERROR_LOG_COOLDOWN_MS) {
          last401ErrorTime = now;
          captureException(error, {
            extra: { context: "Authorization failed - user lacks permissions" },
          });
        }
        throw error;
      }

      // Refresh failed or other error, clear tokens and require re-login
      clearTokens();
      // Note: refreshAccessToken already handles error logging with cooldown
      throw error;
    }
  }

  return response;
};

/**
 * Convenience methods for common HTTP operations
 */
export const http = {
  get: (url: string, options?: Omit<HttpClientOptions, "method">) =>
    httpClient(url, { ...options, method: "GET" }),

  post: (url: string, body?: any, options?: Omit<HttpClientOptions, "method" | "body">) =>
    httpClient(url, { ...options, method: "POST", body }),

  put: (url: string, body?: any, options?: Omit<HttpClientOptions, "method" | "body">) =>
    httpClient(url, { ...options, method: "PUT", body }),

  patch: (url: string, body?: any, options?: Omit<HttpClientOptions, "method" | "body">) =>
    httpClient(url, { ...options, method: "PATCH", body }),

  delete: (url: string, options?: Omit<HttpClientOptions, "method">) =>
    httpClient(url, { ...options, method: "DELETE" }),
};
